import React from 'react'
import { Badge, Button, Table, Td, Th, Tr } from '@/shared/ui'
import { Pagination } from '@/shared/ui/Pagination'
import { formatMoneyFromCents } from '@/shared/i18n/money'
import { cancelRevsharePayout, finalizeRevsharePayout, listRevsharePayoutsPaged } from '../services/revsharePayoutService'
import { monthStartDateToLabel } from '../utils/month'

type Props = {
  userId: string
  refreshKey: number
}

function iso(v?: string | null) {
  return v ? new Date(v).toLocaleString() : '-'
}

export default function RevshareParticipantPayoutsPanel({ userId, refreshKey }: Props) {
  const [items, setItems] = React.useState<any[]>([])
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [total, setTotal] = React.useState(0)
  const [page, setPage] = React.useState(1)
  const pageSize = 25

  const load = React.useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const { rows, total } = await listRevsharePayoutsPaged(userId, page, pageSize)
      setItems(rows)
      setTotal(total)
    } catch (e: any) {
      setError(e?.message || 'Falha ao carregar payouts')
      setItems([])
      setTotal(0)
    } finally {
      setLoading(false)
    }
  }, [userId, page])

  React.useEffect(() => { load() }, [load, refreshKey])

  async function onFinalize(payoutId: string) {
    if (!confirm('Marcar payout como pago?')) return
    try {
      await finalizeRevsharePayout(payoutId)
      await load()
    } catch (e: any) {
      setError(e?.message || 'Falha ao marcar como pago')
    }
  }

  async function onCancel(payoutId: string) {
    if (!confirm('Cancelar payout?')) return
    try {
      await cancelRevsharePayout(payoutId)
      await load()
    } catch (e: any) {
      setError(e?.message || 'Falha ao cancelar')
    }
  }

  return (
    <div className="mt-8 space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-base font-medium">Payouts</div>
        <Button variant="outline" disabled={loading} onClick={load}>
          Atualizar
        </Button>
      </div>

      {error && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive-foreground">
          {error}
        </div>
      )}

      <Table>
        <thead>
          <Tr>
            <Th>Data</Th>
            <Th>Competencia</Th>
            <Th>Status</Th>
            <Th>Valor</Th>
            <Th>Agendado</Th>
            <Th>Pago em</Th>
            <Th className="w-48 text-right">Acoes</Th>
          </Tr>
        </thead>
        <tbody>
          {items.map((p) => {
            const canAct = p.status !== 'paid' && p.status !== 'canceled'
            return (
              <Tr key={p.id}>
                <Td>{iso(p.created_at)}</Td>
                <Td>{monthStartDateToLabel(p.competence_month)}</Td>
                <Td>
                  <Badge variant="soft" className="capitalize">
                    {p.status}
                  </Badge>
                </Td>
                <Td className="font-mono">{formatMoneyFromCents(p.amount_cents, p.currency || 'BRL')}</Td>
                <Td>{iso(p.scheduled_for)}</Td>
                <Td>{iso(p.paid_at)}</Td>
                <Td className="text-right">
                  {canAct ? (
                    <div className="flex items-center justify-end gap-2">
                      <Button className="h-8 px-2 text-xs" onClick={() => onFinalize(p.id)}>
                        Marcar pago
                      </Button>
                      <Button variant="outline" className="h-8 px-2 text-xs" onClick={() => onCancel(p.id)}>
                        Cancelar
                      </Button>
                    </div>
                  ) : (
                    <span className="text-xs text-foreground/60">-</span>
                  )}
                </Td>
              </Tr>
            )
          })}
          {items.length === 0 && (
            <Tr>
              <Td colSpan={7} className="py-6 text-center text-sm text-foreground/70">
                Nenhum payout.
              </Td>
            </Tr>
          )}
        </tbody>
      </Table>

      <div className="flex justify-end">
        <Pagination page={page} total={Math.max(1, Math.ceil(total / pageSize))} onChange={setPage} />
      </div>
    </div>
  )
}

